import QueuesAPI from 'dashboard/api/asterisk/queues';
import { createHandleRealtimeEvent } from './_helpers/asteriskRealtime';
import { createPendingOp } from './_helpers/asteriskAsyncOp';

const RESOURCE = 'queue';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isAddingMember: false,
    isRemovingMember: false,
    isPausingMember: false,
  },
};

export const getters = {
  getRecords: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getByName: $state => name =>
    $state.records.find(
      r => r.queue_name === name || r.queue === name || r.name === name
    ),
  getQueueMembers: $state => name => {
    const queue = $state.records.find(
      r => r.queue_name === name || r.queue === name || r.name === name
    );
    return queue?.members || [];
  },
};

export const actions = {
  // Recebe broadcast ActionCable disparado pelo ApiCallJob (QueuesService) e
  // resolve a Promise pendente do create/update/delete via resolvePendingOp.
  handleRealtimeEvent: createHandleRealtimeEvent('fetch', RESOURCE),

  async fetch({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await QueuesAPI.get();
      const queues =
        response.data.queues || response.data.data || response.data || [];
      commit('SET_RECORDS', queues);
      return queues;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async show(_, queueName) {
    const response = await QueuesAPI.show(queueName);
    return response.data.queue || response.data.data || response.data;
  },

  // Backend retorna 202 (job enfileirado). Promise resolve quando broadcast
  // `asterisk.queue.changed` confirma — permite toast.promise no form.
  async create({ commit }, data) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      await QueuesAPI.create(data);
      await createPendingOp(RESOURCE, 'created', data.queue_name);
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { name, data }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      await QueuesAPI.update(name, data);
      await createPendingOp(RESOURCE, 'updated', name);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, name) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await QueuesAPI.delete(name);
      await createPendingOp(RESOURCE, 'deleted', name);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async addMember({ commit }, { queue, extension, penalty }) {
    commit('SET_UI_FLAG', { isAddingMember: true });
    try {
      await QueuesAPI.addMember(queue, extension, penalty);
      await createPendingOp(RESOURCE, 'updated', queue);
    } finally {
      commit('SET_UI_FLAG', { isAddingMember: false });
    }
  },

  async removeMember({ commit }, { queue, extension }) {
    commit('SET_UI_FLAG', { isRemovingMember: true });
    try {
      await QueuesAPI.removeMember(queue, extension);
      await createPendingOp(RESOURCE, 'updated', queue);
    } finally {
      commit('SET_UI_FLAG', { isRemovingMember: false });
    }
  },

  // pause/unpause são síncronos (200 imediato) — sem createPendingOp.
  // O ActionCable broadcast do controller atualiza outras abas via handleRealtimeEvent.
  async pauseMember({ commit }, { queue, extension, reason = '' }) {
    commit('SET_UI_FLAG', { isPausingMember: true });
    try {
      await QueuesAPI.pauseMember(queue, extension, reason);
    } finally {
      commit('SET_UI_FLAG', { isPausingMember: false });
    }
  },

  async unpauseMember({ commit }, { queue, extension }) {
    commit('SET_UI_FLAG', { isPausingMember: true });
    try {
      await QueuesAPI.unpauseMember(queue, extension);
    } finally {
      commit('SET_UI_FLAG', { isPausingMember: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_RECORDS($state, records) {
    $state.records = records;
  },

  ADD_RECORD($state, record) {
    $state.records.push(record);
  },

  UPDATE_RECORD($state, { name, data }) {
    const index = $state.records.findIndex(
      r => r.queue_name === name || r.queue === name || r.name === name
    );
    if (index !== -1) {
      $state.records[index] = { ...$state.records[index], ...data };
    }
  },

  DELETE_RECORD($state, name) {
    $state.records = $state.records.filter(
      r => r.queue_name !== name && r.queue !== name && r.name !== name
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
